import { useHttpClient, useErrorMonitor } from '@wix/fe-essentials-standalone';
import { useQuery } from '@tanstack/react-query';
import {
  Template,
  TemplateGalleryCard,
  TemplateQueryResponse,
} from '@common/types';
import {
  getBackTemplateId,
  getLastViewedPartnerTemplateId,
} from './usePreviouslyViewedTemplates';

const MAX_RESULTS = 4;
const FETCH_PAGE_SIZE = 20;
const LARGE_PARTNER_CATALOG_THRESHOLD = 6;
const STALE_TIME = 5 * 60 * 1000;

interface FetchParams {
  siteType?: string;
  industries?: string[];
  price?: string;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Appends unique templates to `target` until it reaches `limit`. */
function dedupeInto(
  target: TemplateGalleryCard[],
  candidates: TemplateGalleryCard[],
  seen: Set<string>,
  limit: number,
) {
  for (const t of candidates) {
    if (target.length >= limit) break;
    const id = t.id ?? '';
    if (id && !seen.has(id)) {
      seen.add(id);
      target.push(t);
    }
  }
}

/**
 * Moves a specific template to the first slot of `results`.
 * If the card exists in `results` it is moved; if it only exists in
 * `fallbackPool` it is prepended from there.
 */
function promoteToFirstSlot(
  results: TemplateGalleryCard[],
  templateId: string,
  fallbackPool: Iterable<TemplateGalleryCard>,
) {
  const idx = results.findIndex((t) => t.id === templateId);

  if (idx > 0) {
    const [card] = results.splice(idx, 1);
    results.unshift(card);
  } else if (idx === -1) {
    for (const card of fallbackPool) {
      if (card.id === templateId) {
        results.unshift(card);
        break;
      }
    }
  }
}

function excludeCurrentTemplate(
  templates: TemplateGalleryCard[],
  currentId: string | undefined,
) {
  return (templates ?? []).filter((t) => t.id !== currentId);
}

function excludeCurrentPartner(
  templates: TemplateGalleryCard[],
  accountId: string | undefined,
) {
  return templates.filter((t) => t.accountId !== accountId);
}

function matchesIndustryAndSiteType(
  card: TemplateGalleryCard,
  industries: string[],
  siteType: string,
) {
  return (
    card.industries?.some((i) => industries.includes(i.toLowerCase())) &&
    card.siteTypes?.some((s) => s.toLowerCase() === siteType.toLowerCase())
  );
}

function matchesIndustry(card: TemplateGalleryCard, industries: string[]) {
  return card.industries?.some((i) => industries.includes(i.toLowerCase()));
}

// ---------------------------------------------------------------------------
// useRecommendedTemplates — "You May Also Like"
// ---------------------------------------------------------------------------

/**
 * Builds a prioritized list of filter combinations, from most specific
 * (industry + siteType) to broadest (no filters).
 */
function buildFilterLevels(template: Template): Omit<FetchParams, 'price'>[] {
  const industries = template.industries?.map((i) => i.toLowerCase());
  const siteType = template.siteType?.toLowerCase();

  return [{ industries, siteType }, { industries }, { siteType }, {}];
}

/**
 * Fetches templates through progressively broader filters until
 * `MAX_RESULTS` unique results are collected.
 *
 * Same-price templates are preferred; opposite-price templates fill any
 * remaining slots.
 */
async function fetchRecommendations(
  fetchBatch: (params: FetchParams) => Promise<TemplateGalleryCard[]>,
  template: Template,
) {
  const isCurrentFree = !template.price || template.price === 0;
  const samePriceType = isCurrentFree ? 'free' : 'paid';
  const otherPriceType = isCurrentFree ? 'paid' : 'free';

  const seen = new Set<string>();
  const results: TemplateGalleryCard[] = [];
  const filterLevels = buildFilterLevels(template);

  for (const price of [samePriceType, otherPriceType]) {
    for (const params of filterLevels) {
      if (results.length >= MAX_RESULTS) break;
      const batch = await fetchBatch({ ...params, price });
      dedupeInto(results, batch, seen, MAX_RESULTS);
    }
  }

  return results;
}

export function useRecommendedTemplates(template: Template) {
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();

  return useQuery<TemplateGalleryCard[]>({
    queryKey: ['recommendations', template.id],
    queryFn: async () => {
      // Keeps every fetched card (before partner-exclusion) so we can
      // look up the "back" template even if it was filtered out.
      const allFetched = new Map<string, TemplateGalleryCard>();

      const fetchBatch = async (
        params: FetchParams,
      ): Promise<TemplateGalleryCard[]> => {
        try {
          const { data } = await httpClient.post<TemplateQueryResponse>(
            '/api/templates',
            {
              ...params,
              page: 1,
              sort: 'purchases',
              pageSize: FETCH_PAGE_SIZE,
            },
          );

          const withoutCurrent = excludeCurrentTemplate(
            data.templates,
            template.id,
          );

          for (const t of withoutCurrent) {
            if (t.id) allFetched.set(t.id, t);
          }

          return excludeCurrentPartner(withoutCurrent, template.accountId);
        } catch (err) {
          errorMonitor.captureException(err as Error);
          return [];
        }
      };

      const results = await fetchRecommendations(fetchBatch, template);

      // Reserve slot 0 for the previously-viewed ("back") template.
      const backId = await getBackTemplateId(template.id, errorMonitor);
      if (backId) {
        promoteToFirstSlot(results, backId, allFetched.values());
      }

      return results.slice(0, MAX_RESULTS);
    },
    enabled: !!template.id,
    staleTime: STALE_TIME,
  });
}

// ---------------------------------------------------------------------------
// useMoreByPartnerTemplates — "More by Partner"
// ---------------------------------------------------------------------------

interface MoreByPartnerResult {
  templates: TemplateGalleryCard[];
  totalCount: number;
}

/**
 * When a partner has a large catalog, pick the most relevant templates
 * using the same industry → siteType priority as the main recommendations.
 */
function rankPartnerTemplates(
  partnerTemplates: TemplateGalleryCard[],
  template: Template,
) {
  const seen = new Set<string>();
  const results: TemplateGalleryCard[] = [];
  const industries = template.industries?.map((i) => i.toLowerCase()) ?? [];
  const siteType = template.siteType?.toLowerCase() ?? '';

  // Level 1: matches both industry and siteType
  const byIndustryAndSiteType = partnerTemplates.filter((t) =>
    matchesIndustryAndSiteType(t, industries, siteType),
  );
  dedupeInto(results, byIndustryAndSiteType, seen, MAX_RESULTS);

  // Level 2: matches industry only
  if (results.length < MAX_RESULTS) {
    const byIndustry = partnerTemplates.filter((t) =>
      matchesIndustry(t, industries),
    );
    dedupeInto(results, byIndustry, seen, MAX_RESULTS);
  }

  // Level 3: remaining partner templates
  if (results.length < MAX_RESULTS) {
    dedupeInto(results, partnerTemplates, seen, MAX_RESULTS);
  }

  return results;
}

export function useMoreByPartnerTemplates(template: Template) {
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();
  const accountId = template.accountId;

  return useQuery<MoreByPartnerResult>({
    queryKey: ['more-by-partner', accountId, template.id],
    queryFn: async () => {
      const { data } = await httpClient.post<TemplateQueryResponse>(
        '/api/templates',
        {
          partnerId: accountId,
          page: 1,
          sort: 'purchases',
          pageSize: FETCH_PAGE_SIZE,
          price: 'ALL',
        },
      );

      const partnerTemplates = excludeCurrentTemplate(
        data.templates,
        template.id,
      );
      const totalCount = (data.count ?? 0) - 1;

      if (partnerTemplates.length === 0) {
        return { templates: [], totalCount: 0 };
      }

      // Small catalogs don't need ranking — just show what's available.
      if (data.count <= LARGE_PARTNER_CATALOG_THRESHOLD) {
        return {
          templates: partnerTemplates.slice(0, MAX_RESULTS),
          totalCount,
        };
      }

      const results = rankPartnerTemplates(partnerTemplates, template);

      // Reserve slot 0 for the last partner template the user viewed.
      const lastPartnerViewedId = await getLastViewedPartnerTemplateId(
        template.id,
        accountId,
        errorMonitor,
      );
      if (lastPartnerViewedId) {
        promoteToFirstSlot(results, lastPartnerViewedId, partnerTemplates);
      }

      return { templates: results.slice(0, MAX_RESULTS), totalCount };
    },
    enabled: !!accountId,
    staleTime: STALE_TIME,
  });
}
